// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, Share, StyleSheet, TextInput, View, type ImageSourcePropType } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import QRCode from 'react-native-qrcode-svg';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import {
  getSharedBffWebSocket,
  RoundGroupsUpdateRejectedEvent,
  RoundGroupsUpdatedEvent,
  RoundUpdatedEvent,
} from '@/services/bffWebSocket';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createRequestId } from '@/services/requestId';
import { buildRoundUpdateMessage, type RoundUpdateAiPlayer } from '@/services/roundUpdateMessage';
import { resolveEntryHole } from '@/services/roundNavigation';
import { markRoundEntry } from '@/utils/holeSync';
import { buildGameRuleSetPayload, resolveSelectedRuleSetId } from '@/features/game-selection/ruleSets';
import type { GameRuleSet } from '@/features/game-definitions/types';
import { createAppLogger } from '@/services/appLogger';
import { setCurrentRoundId, setCurrentStartHole } from '@/services/currentRound';
import { isMysteryAcknowledged } from '@/services/mysterySetup';
import { getAssetImageSource } from '@/services/assetImageSource';
import {
  applyPlayerSwap,
  applyTeamMove,
  buildRoundGroupsLayout,
  cycleRoundTeamIndex,
  deriveRoundTeamIndexByPlayerId,
  deriveTeamIndexByPlayerId,
  findUnevenGroupNumbers,
  swapTeamIndexes,
  type RoundGroupsLayout,
} from '@/services/roundGroupsLayout';
import { usesRoundTeams } from '@/features/game-definitions/roundTeams';
import type { GameDefinition as SkippedGameDefinition } from '@/features/game-definitions/types';
import { resolveTourInviteLink } from '@/services/tourInviteLink';
import appColors, { alpha } from '@/theme/colors';
import { parseHandicapValue } from '@/features/hole/utils';
import { normalizeDecimalInput } from '@/utils/decimalInput';
import { holesCountOptions, normalizeHolesCount } from '@/utils/holesCount';
import { movedPastTapSlop } from '@/utils/tapSlop';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';

type RoundResponse = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  status?: string;
  Status?: string;
  tourId?: string;
  TourId?: string;
  holesCount?: number;
  HolesCount?: number;
  gameDefinitionIds?: string[];
  GameDefinitionIds?: string[];
  gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  GameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  gameRuleSets?: Array<{ gameDefinitionId?: string; ruleSetId?: string }>;
  GameRuleSets?: Array<{ gameDefinitionId?: string; ruleSetId?: string }>;
  players?: Array<Record<string, unknown>>;
  Players?: Array<Record<string, unknown>>;
  aiEnabled?: boolean;
  AiEnabled?: boolean;
  aiRevealMode?: string;
  AiRevealMode?: string;
  aiPlayers?: Array<{ playerId?: string; PlayerId?: string; kind?: string; Kind?: string; sourcePlayerId?: string | null; SourcePlayerId?: string | null }>;
  AiPlayers?: Array<{ playerId?: string; PlayerId?: string; kind?: string; Kind?: string; sourcePlayerId?: string | null; SourcePlayerId?: string | null }>;
};

type RoundGroupsResponseTeam = { teamId?: string; TeamId?: string; playerIds?: string[]; PlayerIds?: string[] };
type RoundGroupsResponseGroup = {
  groupId?: string;
  GroupId?: string;
  teams?: RoundGroupsResponseTeam[];
  Teams?: RoundGroupsResponseTeam[];
};

type RoundGroupsResponse = {
  playerOrder?: string[];
  PlayerOrder?: string[];
  canEdit?: boolean;
  CanEdit?: boolean;
  groups?: RoundGroupsResponseGroup[];
  Groups?: RoundGroupsResponseGroup[];
  roundTeams?: RoundGroupsResponseTeam[];
  RoundTeams?: RoundGroupsResponseTeam[];
};

// Normalize a round-teams payload (REST response or WS event, either casing) into the shape
// deriveRoundTeamIndexByPlayerId expects.
const normalizeRoundTeamsPayload = (
  roundTeams: ReadonlyArray<RoundGroupsResponseTeam> | undefined | null
): Array<{ playerIds: string[] }> =>
  (roundTeams ?? []).map((team) => ({
    playerIds: (team.playerIds ?? team.PlayerIds ?? []).filter(
      (playerId): playerId is string => typeof playerId === 'string' && playerId.length > 0
    ),
  }));

// Normalize a groups payload (REST response or WS event, either casing) into the shape
// deriveTeamIndexByPlayerId expects.
const normalizeGroupsPayload = (
  groups: ReadonlyArray<RoundGroupsResponseGroup> | undefined | null
): Array<{ teams: Array<{ playerIds: string[] }> }> =>
  (groups ?? []).map((group) => ({
    teams: (group.teams ?? group.Teams ?? []).map((team) => ({
      playerIds: (team.playerIds ?? team.PlayerIds ?? []).filter(
        (playerId): playerId is string => typeof playerId === 'string' && playerId.length > 0
      ),
    })),
  }));

type RoundPlayer = {
  id: string;
  name: string;
  userId?: string | null;
  isTemporary?: boolean;
  playingHandicap?: number | null;
};

type GameDefinition = {
  id: string;
  name: string;
  info?: string | null;
  description?: string | null;
  competitionType?: string | null;
  image?: string | null;
  video?: string | null;
  videoWebWidth?: number | null;
  videoWebHeight?: number | null;
  videoMobileWidth?: number | null;
  videoMobileHeight?: number | null;
  status?: string | null;
  defaultWeightPercent?: number | null;
  rules?: GameRuleSet[] | null;
};

const MEDIA_PAGE_PATH = '/round/[roundId]/roundstart';
const FALLBACK_GAME_IMAGE = getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);

const GROUP_UPDATE_TIMEOUT_MS = 8000;
type CardBounds = { left: number; top: number; right: number; bottom: number };
type MeasurableCard = { measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void };

export default function RoundStartScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { t } = useI18n();
  const { user } = useUser();
  const { isDeveloper } = useEffectiveRoles(user);
  const router = useRouter();
  // Android draws edge to edge: without the inset the "Open round" button ends under the navigation bar.
  const insets = useSafeAreaInsets();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { roundId: roundIdParam, debug: debugParam, mode: modeParam } = useLocalSearchParams<{ roundId?: string; debug?: string; mode?: string }>();
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const updateMode = useMemo(() => {
    const raw = Array.isArray(modeParam) ? modeParam[0] : modeParam;
    return (raw ?? '').toLowerCase() === 'update';
  }, [modeParam]);
  const roundId = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [roundName, setRoundName] = useState<string | null>(null);
  // Null until the round is loaded, so no option renders as chosen before the real one is known.
  const [holesCount, setHolesCount] = useState<number | null>(null);
  const [tourId, setTourId] = useState<string | null>(null);
  // Where play begins (back-nine / shotgun starts). Detected by tour-service from the round's
  // first score; read here only to land the opener on the right hole.
  const [startHole, setStartHole] = useState<number>(1);
  const [players, setPlayers] = useState<RoundPlayer[]>([]);
  const [roundGames, setRoundGames] = useState<GameDefinition[]>([]);
  // Fourball games the server left out of this round because its field is not a multiple of
  // four. Named on the screen so the organiser sees why, rather than a game silently missing.
  const [skippedGames, setSkippedGames] = useState<SkippedGameDefinition[]>([]);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  // When a round includes the Mystery game but its subgames haven't been assigned yet, send the
  // user to the mystery-prize selection screen on open/start (once). Skipped in round-settings
  // (update) mode so editing settings doesn't bounce away.
  const mysteryRedirectCheckedRef = useRef(false);
  useEffect(() => {
    if (mysteryRedirectCheckedRef.current) return;
    if (loading || updateMode || !roundId || !user?.token) return;
    const hasMystery = roundGames.some(
      (game) => selectedGameIds.has(game.id) && (game.name ?? '').toLowerCase() === 'mystery'
    );
    mysteryRedirectCheckedRef.current = true;
    if (!hasMystery) return;
    let cancelled = false;
    (async () => {
      try {
        // Mystery setup is optional; skip the redirect once the user has saved or skipped it for
        // this round (the assignment write is async, so subgames may still read as empty).
        if (await isMysteryAcknowledged(roundId)) return;
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as unknown;
        const count = Array.isArray(data) ? data.length : 0;
        if (!cancelled && count === 0) {
          router.replace(`/round/${roundId}/mystery-prize?tourId=${tourId ?? ''}&debug=${debug ? 'true' : 'false'}`);
        }
      } catch {
        // Ignore; the mystery prize can still be set from the menu.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, loading, roundGames, selectedGameIds, updateMode, roundId, user?.token, router, tourId, debug]);
  const [weightsByGameId, setWeightsByGameId] = useState<Record<string, number>>({});
  // Sparse: no entry means the game inherits, resolved at render by resolveSelectedRuleSetId.
  const [ruleSetByGameId, setRuleSetByGameId] = useState<Record<string, string>>({});
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiRevealMode, setAiRevealMode] = useState<'in_advance' | 'on_score'>('on_score');
  const [aiPlayers, setAiPlayers] = useState<RoundUpdateAiPlayer[]>([]);
  const [selectedPlayersById, setSelectedPlayersById] = useState<Record<string, boolean>>({});
  const [playingHandicapByPlayerId, setPlayingHandicapByPlayerId] = useState<Record<string, string>>({});
  const [savedPlayingHandicapByPlayerId, setSavedPlayingHandicapByPlayerId] = useState<Record<string, string>>({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);
  const [openingRound, setOpeningRound] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalLink, setShareModalLink] = useState<string | null>(null);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalError, setShareModalError] = useState<string | null>(null);
  const [groupLayout, setGroupLayout] = useState<RoundGroupsLayout | null>(null);
  const [groupCanEdit, setGroupCanEdit] = useState(true);
  const [selectedSwapPlayerId, setSelectedSwapPlayerId] = useState<string | null>(null);
  const [groupSavePending, setGroupSavePending] = useState(false);
  const [groupSaveError, setGroupSaveError] = useState<string | null>(null);
  // Where the last "mix the balls" request stands, shown under its button.
  const [groupMixStatus, setGroupMixStatus] = useState<'mixing' | 'mixed' | 'noHistory' | null>(null);
  const [draggingPlayerId, setDraggingPlayerId] = useState<string | null>(null);
  const [dragHoverPlayerId, setDragHoverPlayerId] = useState<string | null>(null);
  const [dragHoverTeamZoneGroupIndex, setDragHoverTeamZoneGroupIndex] = useState<number | null>(null);
  const [teamIndexById, setTeamIndexById] = useState<Record<string, number>>({});
  // Ref mirror so callbacks always see the latest map without re-subscribing.
  const teamIndexByIdRef = useRef<Record<string, number>>({});
  // Round-level teams (Best of Team): a player's team number independent of their group, so a
  // team may span balls. Kept apart from teamIndexById, which is the pair inside a group.
  const [roundTeamIndexById, setRoundTeamIndexById] = useState<Record<string, number>>({});
  const roundTeamIndexByIdRef = useRef<Record<string, number>>({});
  const pendingGroupUpdateRef = useRef<{
    requestId: string;
    // 'mix' asked the server to mix the balls; anything else saved the user's own arrangement.
    kind?: 'mix';
    previousOrder: string[];
    previousTeamIndexById: Record<string, number>;
    previousRoundTeamIndexById: Record<string, number>;
  } | null>(null);
  const groupUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last arrangement confirmed by the server — the rollback target for failed saves.
  const lastSavedGroupsRef = useRef<{
    order: string[];
    teamIndexById: Record<string, number>;
    roundTeamIndexById: Record<string, number>;
  } | null>(null);
  const playerCardRefs = useRef<Record<string, MeasurableCard | null>>({});
  const playerCardBoundsRef = useRef<Record<string, CardBounds>>({});
  const dragOriginRef = useRef<{ pageX: number; pageY: number } | null>(null);
  const teamZoneRefs = useRef<Record<number, MeasurableCard | null>>({});
  const teamZoneBoundsRef = useRef<Record<number, CardBounds>>({});

  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'round-start',
      }),
    [apiBaseUrl, user?.token]
  );
  const playerNameById = useMemo(() => {
    const next: Record<string, string> = {};
    players.forEach((player) => {
      next[player.id] = player.name || player.id;
    });
    return next;
  }, [players]);

  const updateTeamIndexById = useCallback((next: Record<string, number>) => {
    teamIndexByIdRef.current = next;
    setTeamIndexById(next);
  }, []);
  const updateRoundTeamIndexById = useCallback((next: Record<string, number>) => {
    roundTeamIndexByIdRef.current = next;
    setRoundTeamIndexById(next);
  }, []);
  // The Teams board only exists for a game that plays round-level teams; every other team game
  // takes its pairs from the groups above.
  const hasRoundTeamGame = useMemo(
    () => roundGames.some((game) => selectedGameIds.has(game.id) && usesRoundTeams(game)),
    [roundGames, selectedGameIds]
  );

  // Groups whose team split the user must even out (e.g. 3v1). While any exist the
  // arrangement stays local-only: nothing is sent to the server and the round can't start.
  const unevenGroupNumbers = useMemo(
    () => (groupLayout ? findUnevenGroupNumbers(groupLayout) : []),
    [groupLayout]
  );

  const loadSnapshot = useCallback(async () => {
    if (!roundId || !user?.token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [roundResponse, playersResponse, gameDefinitionsResponse, groupsResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/groups`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
      ]);

      if (!roundResponse.ok || !playersResponse.ok || !gameDefinitionsResponse.ok) {
        throw new Error(t('roundStartSetup.loadError'));
      }

      const roundData = (await roundResponse.json()) as RoundResponse;
      const playersData = await playersResponse.json();
      const gameDefinitionsRaw = (await gameDefinitionsResponse.json()) as Array<Record<string, unknown>>;
      const groupsData = groupsResponse.ok
        ? ((await groupsResponse.json()) as RoundGroupsResponse)
        : null;

      const normalizedRoundName = (roundData.name ?? roundData.Name ?? '').trim();
      setRoundName(normalizedRoundName || null);
      setHolesCount(normalizeHolesCount(roundData.holesCount ?? roundData.HolesCount));
      setTourId((roundData.tourId ?? roundData.TourId ?? null) as string | null);

      const rawRound = roundData as Record<string, unknown>;
      const loadedStartHole = Number(rawRound.startHoleNumber ?? rawRound.StartHoleNumber);
      if (Number.isInteger(loadedStartHole) && loadedStartHole >= 1) {
        setStartHole(loadedStartHole);
        setCurrentStartHole(loadedStartHole);
      }

      const roundGameIds = Array.from(
        new Set((roundData.gameDefinitionIds ?? roundData.GameDefinitionIds ?? []).filter((id): id is string => typeof id === 'string' && id.length > 0))
      );

      const playersPayload = Array.isArray(playersData) ? playersData : (playersData.players ?? playersData.Players ?? []);
      const roundPlayersPayload = Array.isArray(roundData.players)
        ? roundData.players
        : (Array.isArray(roundData.Players) ? roundData.Players : []);
      const roundHandicapByPlayerId = new Map<string, number | null>();
      roundPlayersPayload.forEach((entry: Record<string, unknown>) => {
        const playerId = String(entry.id ?? entry.Id ?? entry.playerId ?? entry.PlayerId ?? '');
        if (!playerId) return;
        const handicap = parseHandicapValue(
          entry.playingHandicap ??
            entry.PlayingHandicap ??
            entry.playing_handicap ??
            entry.Playing_Handicap ??
            entry.shcp ??
            entry.Shcp ??
            entry.scp ??
            entry.Scp
        );
        roundHandicapByPlayerId.set(playerId, handicap ?? null);
      });

      const normalizedPlayers = playersPayload
        .map((entry: Record<string, unknown>) => {
          const playerId = String(entry.id ?? entry.Id ?? '');
          const roundScopedHandicap = roundHandicapByPlayerId.get(playerId);
          const playerScopedHandicap = parseHandicapValue(
            entry.playingHandicap ??
              entry.PlayingHandicap ??
              entry.playing_handicap ??
              entry.Playing_Handicap ??
              entry.shcp ??
              entry.Shcp ??
              entry.scp ??
              entry.Scp
          );
          return {
            id: playerId,
            name: String(entry.name ?? entry.Name ?? ''),
            userId: (entry.userId ?? entry.UserId ?? null) as string | null,
            isTemporary: Boolean(entry.isTemporary ?? entry.IsTemporary ?? false),
            // Always prefer round-specific SHCP when available for this player.
            playingHandicap: roundScopedHandicap !== undefined ? roundScopedHandicap : (playerScopedHandicap ?? null),
          };
        })
        .filter((entry: RoundPlayer) => !!entry.id);
      setPlayers(normalizedPlayers);
      setSelectedPlayersById((prev) => {
        const next: Record<string, boolean> = {};
        normalizedPlayers.forEach((player) => {
          next[player.id] = prev[player.id] ?? true;
        });
        return next;
      });
      const baselinePlayerOrder = normalizedPlayers.map((player) => player.id);
      const incomingManualOrder = groupsData
        ? (groupsData.playerOrder ?? groupsData.PlayerOrder ?? []).filter((playerId): playerId is string => typeof playerId === 'string')
        : null;
      setGroupCanEdit(
        groupsData
          ? Boolean(groupsData.canEdit ?? groupsData.CanEdit ?? ((roundData.status ?? roundData.Status ?? '').toLowerCase() === 'draft'))
          : ((roundData.status ?? roundData.Status ?? '').toLowerCase() === 'draft')
      );
      const incomingTeamIndexById = deriveTeamIndexByPlayerId(
        normalizeGroupsPayload(groupsData?.groups ?? groupsData?.Groups)
      );
      updateTeamIndexById(incomingTeamIndexById);
      const incomingRoundTeamIndexById = deriveRoundTeamIndexByPlayerId(
        normalizeRoundTeamsPayload(groupsData?.roundTeams ?? groupsData?.RoundTeams)
      );
      updateRoundTeamIndexById(incomingRoundTeamIndexById);
      const incomingLayout = buildRoundGroupsLayout(roundId, baselinePlayerOrder, incomingManualOrder, incomingTeamIndexById);
      setGroupLayout(incomingLayout);
      lastSavedGroupsRef.current = {
        order: [...incomingLayout.effectivePlayerOrder],
        teamIndexById: incomingTeamIndexById,
        roundTeamIndexById: incomingRoundTeamIndexById,
      };
      setSelectedSwapPlayerId(null);
      setGroupSavePending(false);
      setGroupSaveError(null);
      pendingGroupUpdateRef.current = null;
      setPlayingHandicapByPlayerId((prev) => {
        const next: Record<string, string> = {};
        normalizedPlayers.forEach((player) => {
          if (typeof player.playingHandicap === 'number' && Number.isFinite(player.playingHandicap)) {
            next[player.id] = String(player.playingHandicap);
            return;
          }
          const existing = prev[player.id];
          next[player.id] = typeof existing === 'string' ? existing : '';
        });
        return next;
      });
      setSavedPlayingHandicapByPlayerId(() => {
        const next: Record<string, string> = {};
        normalizedPlayers.forEach((player) => {
          if (typeof player.playingHandicap === 'number' && Number.isFinite(player.playingHandicap)) {
            next[player.id] = String(player.playingHandicap);
          } else {
            next[player.id] = '';
          }
        });
        return next;
      });

      const definitions = (gameDefinitionsRaw ?? [])
        .map((entry) => ({
          id: String(entry['id'] ?? entry['Id'] ?? ''),
          name: String(entry['name'] ?? entry['Name'] ?? ''),
          info: (entry['info'] as string | null | undefined) ?? (entry['Info'] as string | null | undefined) ?? null,
          description:
            (entry['description'] as string | null | undefined) ??
            (entry['Description'] as string | null | undefined) ??
            null,
          competitionType:
            (entry['competitionType'] as string | null | undefined) ??
            (entry['CompetitionType'] as string | null | undefined) ??
            null,
          image: (entry['image'] as string | null | undefined) ?? (entry['Image'] as string | null | undefined) ?? null,
          video: (entry['video'] as string | null | undefined) ?? (entry['Video'] as string | null | undefined) ?? null,
          videoWebWidth:
            (entry['videoWebWidth'] as number | null | undefined) ??
            (entry['VideoWebWidth'] as number | null | undefined) ??
            null,
          videoWebHeight:
            (entry['videoWebHeight'] as number | null | undefined) ??
            (entry['VideoWebHeight'] as number | null | undefined) ??
            null,
          videoMobileWidth:
            (entry['videoMobileWidth'] as number | null | undefined) ??
            (entry['VideoMobileWidth'] as number | null | undefined) ??
            null,
          videoMobileHeight:
            (entry['videoMobileHeight'] as number | null | undefined) ??
            (entry['VideoMobileHeight'] as number | null | undefined) ??
            null,
          status: (entry['status'] as string | null | undefined) ?? (entry['Status'] as string | null | undefined) ?? null,
          rules:
            (entry['rules'] as GameDefinition['rules']) ??
            (entry['Rules'] as GameDefinition['rules']) ??
            null,
          defaultWeightPercent:
            (entry['defaultWeightPercent'] as number | null | undefined) ??
            (entry['DefaultWeightPercent'] as number | null | undefined) ??
            100,
        }))
        .filter((entry) => !!entry.id);

      const definitionsById = new Map(definitions.map((entry) => [entry.id, entry]));
      const mappedRoundGames = roundGameIds.map((gameId) => {
        const definition = definitionsById.get(gameId);
        return definition ?? {
          id: gameId,
          name: gameId,
          status: null,
          defaultWeightPercent: 100,
          info: null,
          description: null,
          competitionType: null,
          image: null,
          video: null,
        };
      });
      setRoundGames(mappedRoundGames);
      const skippedRaw = roundData as unknown as {
        skippedGameDefinitionIds?: unknown[];
        SkippedGameDefinitionIds?: unknown[];
      };
      const skippedGameIds = (skippedRaw.skippedGameDefinitionIds ?? skippedRaw.SkippedGameDefinitionIds ?? [])
        .map((gameId) => String(gameId))
        .filter((gameId) => gameId.length > 0);
      setSkippedGames(
        skippedGameIds.map((gameId) => definitionsById.get(gameId) ?? {
          id: gameId,
          name: gameId,
          status: null,
          defaultWeightPercent: 100,
          info: null,
          description: null,
          competitionType: null,
          image: null,
          video: null,
        })
      );
      setSelectedGameIds(new Set(roundGameIds));

      const rawWeights = roundData.gameWeights ?? roundData.GameWeights ?? [];
      const mappedWeights: Record<string, number> = {};
      rawWeights.forEach((entry) => {
        if (entry?.gameDefinitionId && Number.isFinite(entry.weightPercent)) {
          mappedWeights[entry.gameDefinitionId] = Number(entry.weightPercent);
        }
      });
      mappedRoundGames.forEach((game) => {
        if (!Number.isFinite(mappedWeights[game.id])) {
          mappedWeights[game.id] = Number(game.defaultWeightPercent ?? 100);
        }
      });
      setWeightsByGameId(mappedWeights);

      const rawRuleSets = roundData.gameRuleSets ?? roundData.GameRuleSets ?? [];
      const mappedRuleSets: Record<string, string> = {};
      rawRuleSets.forEach((entry) => {
        if (entry?.gameDefinitionId && typeof entry.ruleSetId === 'string' && entry.ruleSetId.length > 0) {
          mappedRuleSets[entry.gameDefinitionId] = entry.ruleSetId;
        }
      });
      setRuleSetByGameId(mappedRuleSets);

      setAiEnabled(Boolean(roundData.aiEnabled ?? roundData.AiEnabled ?? false));
      const revealModeRaw = roundData.aiRevealMode ?? roundData.AiRevealMode ?? 'on_score';
      setAiRevealMode(revealModeRaw === 'in_advance' ? 'in_advance' : 'on_score');
      const rawAiPlayers = roundData.aiPlayers ?? roundData.AiPlayers ?? [];
      setAiPlayers(
        rawAiPlayers
          .map((entry) => ({
            playerId: String(entry.playerId ?? entry.PlayerId ?? ''),
            kind: (entry.kind ?? entry.Kind) === 'ghost' ? ('ghost' as const) : ('pure' as const),
            sourcePlayerId: (entry.sourcePlayerId ?? entry.SourcePlayerId) ?? null,
          }))
          .filter((entry) => entry.playerId.length > 0)
      );

      setStatusNote('');
    } catch (loadErr) {
      setError(loadErr instanceof Error ? loadErr.message : t('roundStartSetup.loadError'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, roundId, t, user?.token]);

  const sendRoundUpdate = useCallback(
    (
      nextSelectedGameIds: Set<string>,
      overrideWeightsByGameId?: Record<string, number>,
      overrideRuleSetByGameId?: Record<string, string>,
    ) => {
      if (!roundId || !user?.token) return;
      const orderedGameIds = roundGames.map((game) => game.id).filter((id) => nextSelectedGameIds.has(id));
      const weights = overrideWeightsByGameId ?? weightsByGameId;
      // The override argument exists because this screen persists on every change: setState is
      // async, so reading state here would send the value from before the tap.
      const ruleSets = overrideRuleSetByGameId ?? ruleSetByGameId;
      const payload = buildRoundUpdateMessage({
        roundId,
        gameDefinitionIds: orderedGameIds,
        gameWeights: orderedGameIds.map((id) => ({
          gameDefinitionId: id,
          weightPercent: Number.isFinite(weights[id]) ? weights[id] : 100,
        })),
        gameRuleSets: buildGameRuleSetPayload({
          gameDefinitionIds: orderedGameIds,
          ruleSetByGameId: ruleSets,
          gameDefs: roundGames,
        }),
      });
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      const sent = socket.send(payload);
      setStatusNote(sent === 'sent' ? t('roundStartSetup.gamesUpdateSent') : t('roundStartSetup.gamesUpdateQueued'));
      logToBff('roundstart.round.update', {
        roundId,
        sent,
        gameCount: orderedGameIds.length,
      });
    },
    [apiBaseUrl, logToBff, roundGames, roundId, ruleSetByGameId, t, user?.token, weightsByGameId]
  );

  const selectGameRuleSet = useCallback(
    (gameId: string, ruleSetId: string) => {
      setRuleSetByGameId((prev) => {
        const next = { ...prev, [gameId]: ruleSetId };
        // Persisted immediately, like every other change on this screen — the round is already
        // live here, so there is no "start" button to batch the save behind.
        sendRoundUpdate(selectedGameIds, undefined, next);
        return next;
      });
    },
    [selectedGameIds, sendRoundUpdate]
  );

  const handleSelectHolesCount = useCallback(
    (count: number) => {
      if (!roundId || !user?.token || count === holesCount) return;
      setHolesCount(count);
      // Persisted on the tap, like the AI reveal mode and the game selection either side of it:
      // this screen has no save button of its own. The round.updated echo corrects the value if
      // the server settles on a different one (it coerces 3 back to 18 for non-developers).
      const payload = buildRoundUpdateMessage({ roundId, holesCount: count });
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      const sent = socket.send(payload);
      setStatusNote(sent === 'sent' ? t('roundStartSetup.gamesUpdateSent') : t('roundStartSetup.gamesUpdateQueued'));
      logToBff('roundstart.round.update', { roundId, sent, holesCount: count });
    },
    [apiBaseUrl, holesCount, logToBff, roundId, t, user?.token]
  );

  const handleSelectAiRevealMode = useCallback(
    (mode: 'in_advance' | 'on_score') => {
      if (!roundId || !user?.token) return;
      setAiRevealMode(mode);
      // A dedicated message rather than sendRoundUpdate: the ai* fields are independent of the
      // game selection this screen otherwise persists, and aiPlayers must always be sent whenever
      // aiEnabled is (buildRoundUpdateMessage), so the current AI player list travels along too.
      const payload = buildRoundUpdateMessage({
        roundId,
        aiEnabled,
        aiRevealMode: mode,
        aiPlayers,
      });
      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      const sent = socket.send(payload);
      setStatusNote(sent === 'sent' ? t('roundStartSetup.gamesUpdateSent') : t('roundStartSetup.gamesUpdateQueued'));
      logToBff('roundstart.round.update', { roundId, sent, aiRevealMode: mode });
    },
    [aiEnabled, aiPlayers, apiBaseUrl, logToBff, roundId, t, user?.token]
  );

  const clearGroupUpdateTimeout = useCallback(() => {
    if (!groupUpdateTimeoutRef.current) return;
    clearTimeout(groupUpdateTimeoutRef.current);
    groupUpdateTimeoutRef.current = null;
  }, []);

  const applyGroupOrder = useCallback(
    (
      playerOrder: string[],
      nextTeamIndexById?: Record<string, number>,
      nextRoundTeamIndexById?: Record<string, number>
    ) => {
      if (nextTeamIndexById) {
        updateTeamIndexById(nextTeamIndexById);
      }
      if (nextRoundTeamIndexById) {
        updateRoundTeamIndexById(nextRoundTeamIndexById);
      }
      const effectiveTeamIndexById = nextTeamIndexById ?? teamIndexByIdRef.current;
      setGroupLayout((previousLayout) => {
        if (!previousLayout) return previousLayout;
        if (!roundId) return previousLayout;
        return buildRoundGroupsLayout(roundId, previousLayout.baselinePlayerOrder, playerOrder, effectiveTeamIndexById);
      });
    },
    [roundId, updateRoundTeamIndexById, updateTeamIndexById]
  );

  type DropTarget = { kind: 'player'; playerId: string } | { kind: 'teamZone'; groupIndex: number };

  const resolveDropTarget = useCallback((pageX: number, pageY: number, sourcePlayerId: string): DropTarget | null => {
    for (const [groupIndex, bounds] of Object.entries(teamZoneBoundsRef.current)) {
      if (pageX >= bounds.left && pageX <= bounds.right && pageY >= bounds.top && pageY <= bounds.bottom) {
        return { kind: 'teamZone', groupIndex: Number(groupIndex) };
      }
    }
    const entries = Object.entries(playerCardBoundsRef.current);
    for (const [playerId, bounds] of entries) {
      if (playerId === sourcePlayerId) continue;
      if (pageX >= bounds.left && pageX <= bounds.right && pageY >= bounds.top && pageY <= bounds.bottom) {
        return { kind: 'player', playerId };
      }
    }
    return null;
  }, []);

  const refreshPlayerCardBounds = useCallback(() => {
    // measureInWindow is viewport-relative on web while responder pageX/pageY are
    // document-relative, so add the scroll offset or drops miss once the page scrolls.
    const scrollX = Platform.OS === 'web' ? (globalThis as { scrollX?: number }).scrollX ?? 0 : 0;
    const scrollY = Platform.OS === 'web' ? (globalThis as { scrollY?: number }).scrollY ?? 0 : 0;
    Object.entries(playerCardRefs.current).forEach(([playerId, cardRef]) => {
      cardRef?.measureInWindow?.((x, y, width, height) => {
        if (!width || !height) return;
        playerCardBoundsRef.current[playerId] = {
          left: x + scrollX,
          top: y + scrollY,
          right: x + scrollX + width,
          bottom: y + scrollY + height,
        };
      });
    });
    teamZoneBoundsRef.current = {};
    Object.entries(teamZoneRefs.current).forEach(([groupIndex, zoneRef]) => {
      zoneRef?.measureInWindow?.((x, y, width, height) => {
        if (!width || !height) return;
        teamZoneBoundsRef.current[Number(groupIndex)] = {
          left: x + scrollX,
          top: y + scrollY,
          right: x + scrollX + width,
          bottom: y + scrollY + height,
        };
      });
    });
  }, []);

  // The only rollback path for a groups request whose answer never comes: back to the arrangement
  // the server last confirmed.
  const startGroupUpdateTimeout = useCallback(
    (requestId: string) => {
      clearGroupUpdateTimeout();
      groupUpdateTimeoutRef.current = setTimeout(() => {
        const pending = pendingGroupUpdateRef.current;
        if (!pending || pending.requestId !== requestId) return;
        pendingGroupUpdateRef.current = null;
        applyGroupOrder(pending.previousOrder, pending.previousTeamIndexById, pending.previousRoundTeamIndexById);
        setGroupSavePending(false);
        setGroupMixStatus(null);
        setGroupSaveError(t('roundStartSetup.groupsUpdateTimeout'));
        setStatusNote(t('roundStartSetup.groupsUpdateTimeout'));
      }, GROUP_UPDATE_TIMEOUT_MS);
    },
    [applyGroupOrder, clearGroupUpdateTimeout, t]
  );

  const publishGroupOrderUpdate = useCallback(
    (nextOrder: string[], nextTeamIndexById: Record<string, number>) => {
      if (!roundId || !user?.token) return;
      const lastSaved = lastSavedGroupsRef.current;
      const requestId = createRequestId();
      // Round teams always travel with a save: the server replaces the whole slot set, so leaving
      // them out would clear them.
      const nextRoundTeamIndexById = roundTeamIndexByIdRef.current;
      pendingGroupUpdateRef.current = {
        requestId,
        previousOrder: lastSaved?.order ?? nextOrder,
        previousTeamIndexById: lastSaved?.teamIndexById ?? nextTeamIndexById,
        previousRoundTeamIndexById: lastSaved?.roundTeamIndexById ?? nextRoundTeamIndexById,
      };
      setGroupSavePending(true);
      setGroupSaveError(null);
      setGroupMixStatus(null);
      setStatusNote(t('roundStartSetup.groupsUpdateSending'));

      const socket = getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user.token);
      // 'queued' is not a failure — the frame is flushed when the socket (re)opens and the update
      // is still in flight. The timeout below is the only rollback path.
      socket.send({
        type: 'round.groups.update',
        requestId,
        roundId,
        playerOrder: nextOrder,
        teamAssignments: Object.entries(nextTeamIndexById).map(([playerId, teamIndex]) => ({
          playerId,
          teamIndex,
        })),
        roundTeamAssignments: Object.entries(nextRoundTeamIndexById).map(([playerId, teamIndex]) => ({
          playerId,
          teamIndex,
        })),
      });

      startGroupUpdateTimeout(requestId);
    },
    [apiBaseUrl, roundId, startGroupUpdateTimeout, t, user?.token]
  );

  // Ask the server to put the players together with new company: as many different ball-mates,
  // then teammates, as possible across the tour. The answer arrives like a save's, as
  // round.groups.updated or .rejected, so nothing changes locally until then.
  const handleMixGroups = useCallback(() => {
    if (!roundId || !user?.token || !groupCanEdit || !groupLayout || groupSavePending) return;
    const requestId = createRequestId();
    pendingGroupUpdateRef.current = {
      requestId,
      kind: 'mix',
      previousOrder: [...groupLayout.effectivePlayerOrder],
      previousTeamIndexById: teamIndexByIdRef.current,
      previousRoundTeamIndexById: roundTeamIndexByIdRef.current,
    };
    setGroupSavePending(true);
    setGroupSaveError(null);
    setSelectedSwapPlayerId(null);
    setGroupMixStatus('mixing');

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    socket.send({ type: 'round.groups.mix', requestId, roundId });
    startGroupUpdateTimeout(requestId);
  }, [apiBaseUrl, groupCanEdit, groupLayout, groupSavePending, roundId, startGroupUpdateTimeout, user?.token]);

  // Apply an arrangement locally and save it — unless a group came out uneven (e.g. 3v1):
  // then it stays local-only with a warning until the user evens the teams out.
  const commitGroupArrangement = useCallback(
    (nextOrder: string[], nextTeamIndexById: Record<string, number>) => {
      if (!roundId) return;
      const nextLayout = buildRoundGroupsLayout(
        roundId,
        groupLayout?.baselinePlayerOrder ?? nextOrder,
        nextOrder,
        nextTeamIndexById
      );
      updateTeamIndexById(nextTeamIndexById);
      setGroupLayout(nextLayout);
      if (findUnevenGroupNumbers(nextLayout).length > 0) {
        setStatusNote(t('roundStartSetup.groupsUneven'));
        return;
      }
      publishGroupOrderUpdate(nextOrder, nextTeamIndexById);
    },
    [groupLayout, publishGroupOrderUpdate, roundId, t, updateTeamIndexById]
  );

  // Tap a player on the Teams board: move them to the next round team (see cycleRoundTeamIndex)
  // and save. The groups are sent unchanged; an uneven group blocks this save like any other.
  const handleRoundTeamPress = useCallback(
    (playerId: string) => {
      if (!roundId || !groupCanEdit || !groupLayout || groupSavePending) return;
      if (findUnevenGroupNumbers(groupLayout).length > 0) {
        setStatusNote(t('roundStartSetup.groupsUneven'));
        return;
      }
      updateRoundTeamIndexById(cycleRoundTeamIndex(roundTeamIndexByIdRef.current, playerId));
      publishGroupOrderUpdate([...groupLayout.effectivePlayerOrder], teamIndexByIdRef.current);
    },
    [groupCanEdit, groupLayout, groupSavePending, publishGroupOrderUpdate, roundId, t, updateRoundTeamIndexById]
  );

  const findGroupIndexOfPlayer = useCallback(
    (playerId: string): number =>
      groupLayout ? groupLayout.groups.findIndex((group) => group.playerIds.includes(playerId)) : -1,
    [groupLayout]
  );

  const handleGroupPlayerPress = useCallback(
    (playerId: string) => {
      if (!roundId || !groupCanEdit || !groupLayout || groupSavePending) return;

      if (!selectedSwapPlayerId) {
        setSelectedSwapPlayerId(playerId);
        return;
      }

      if (selectedSwapPlayerId === playerId) {
        setSelectedSwapPlayerId(null);
        return;
      }

      const previousOrder = [...groupLayout.effectivePlayerOrder];
      const nextOrder = applyPlayerSwap(previousOrder, selectedSwapPlayerId, playerId);
      const nextTeamIndexById = swapTeamIndexes(teamIndexByIdRef.current, selectedSwapPlayerId, playerId);
      setSelectedSwapPlayerId(null);
      if (nextOrder.join('|') === previousOrder.join('|')) {
        return;
      }

      commitGroupArrangement(nextOrder, nextTeamIndexById);
    },
    [commitGroupArrangement, groupCanEdit, groupLayout, groupSavePending, roundId, selectedSwapPlayerId]
  );

  const handleDragStart = useCallback((playerId: string) => {
    if (!groupCanEdit || groupSavePending || !groupLayout) {
      return;
    }
    refreshPlayerCardBounds();
    setDraggingPlayerId(playerId);
    setDragHoverPlayerId(null);
    setDragHoverTeamZoneGroupIndex(null);
  }, [groupCanEdit, groupLayout, groupSavePending, refreshPlayerCardBounds]);

  const handleDragMove = useCallback((playerId: string, pageX: number, pageY: number) => {
    if (draggingPlayerId !== playerId) return;
    const target = resolveDropTarget(pageX, pageY, playerId);
    setDragHoverPlayerId(target?.kind === 'player' ? target.playerId : null);
    setDragHoverTeamZoneGroupIndex(target?.kind === 'teamZone' ? target.groupIndex : null);
  }, [draggingPlayerId, resolveDropTarget]);

  const handleDragRelease = useCallback((playerId: string, pageX: number, pageY: number) => {
    if (!roundId || !groupCanEdit || !groupLayout || groupSavePending || draggingPlayerId !== playerId) {
      setDraggingPlayerId(null);
      setDragHoverPlayerId(null);
      setDragHoverTeamZoneGroupIndex(null);
      return;
    }

    const target = resolveDropTarget(pageX, pageY, playerId);
    setDraggingPlayerId(null);
    setDragHoverPlayerId(null);
    setDragHoverTeamZoneGroupIndex(null);
    if (!target) {
      return;
    }
    setSelectedSwapPlayerId(null);

    const previousOrder = [...groupLayout.effectivePlayerOrder];
    const sourceGroupIndex = findGroupIndexOfPlayer(playerId);

    if (target.kind === 'teamZone') {
      // Drop on the empty-team zone: start a new second team in the player's own group.
      if (target.groupIndex !== sourceGroupIndex) return;
      const group = groupLayout.groups[target.groupIndex];
      if (!group || group.teams.length !== 1) return;
      const nextTeamIndexById = applyTeamMove(
        groupLayout,
        teamIndexByIdRef.current,
        playerId,
        target.groupIndex,
        1
      );
      if (!nextTeamIndexById) return;
      commitGroupArrangement(previousOrder, nextTeamIndexById);
      return;
    }

    if (target.playerId === playerId) {
      return;
    }

    const targetGroupIndex = findGroupIndexOfPlayer(target.playerId);
    if (sourceGroupIndex >= 0 && sourceGroupIndex === targetGroupIndex) {
      // Same group: move the dragged player into the target card's team.
      const group = groupLayout.groups[targetGroupIndex];
      const targetTeamIndex = group.teams.findIndex((team) => team.playerIds.includes(target.playerId));
      if (targetTeamIndex < 0 || group.teams[targetTeamIndex].playerIds.includes(playerId)) return;
      const nextTeamIndexById = applyTeamMove(
        groupLayout,
        teamIndexByIdRef.current,
        playerId,
        targetGroupIndex,
        targetTeamIndex
      );
      if (!nextTeamIndexById) return;
      commitGroupArrangement(previousOrder, nextTeamIndexById);
      return;
    }

    // Across groups the drop stays a swap: the players trade positions and team slots.
    const nextOrder = applyPlayerSwap(previousOrder, playerId, target.playerId);
    if (nextOrder.join('|') === previousOrder.join('|')) {
      return;
    }
    const nextTeamIndexById = swapTeamIndexes(teamIndexByIdRef.current, playerId, target.playerId);
    commitGroupArrangement(nextOrder, nextTeamIndexById);
  }, [commitGroupArrangement, draggingPlayerId, findGroupIndexOfPlayer, groupCanEdit, groupLayout, groupSavePending, resolveDropTarget, roundId]);

  const handleDragCancel = useCallback(() => {
    setDraggingPlayerId(null);
    setDragHoverPlayerId(null);
    setDragHoverTeamZoneGroupIndex(null);
  }, []);

  const handleTogglePlayerSelection = useCallback((playerId: string) => {
    setSelectedPlayersById((prev) => ({
      ...prev,
      [playerId]: !(prev[playerId] ?? true),
    }));
  }, []);

  const handlePlayingHandicapChange = useCallback((playerId: string, value: string) => {
    // A comma-locale keyboard sends "," as the decimal separator. Map it to "."
    // before stripping, otherwise "18,5" silently collapses into "185".
    const normalized = normalizeDecimalInput(value).replace(/[^0-9+\-.]/g, '');
    setPlayingHandicapByPlayerId((prev) => ({
      ...prev,
      [playerId]: normalized,
    }));
  }, []);

  const handleToggleGame = useCallback(
    (gameId: string) => {
      const next = new Set(selectedGameIds);
      if (next.has(gameId)) {
        next.delete(gameId);
      } else {
        next.add(gameId);
      }
      setSelectedGameIds(next);
      sendRoundUpdate(next);
    },
    [selectedGameIds, sendRoundUpdate]
  );

  const closeInfo = useCallback(() => {
    setInfoOpen(false);
    setInfoGame(null);
  }, []);

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  const resolveGameImageSource = useCallback((game: GameDefinition): ImageSourcePropType => {
    if (game.image && game.image.trim().length > 0) {
      return getAssetImageSource(game.image, 'game-select', MEDIA_PAGE_PATH);
    }
    return FALLBACK_GAME_IMAGE;
  }, []);

  const waitForRoundStatus = useCallback(
    async (expectedStatus: string, timeoutMs = 10000, intervalMs = 350): Promise<boolean> => {
      if (!roundId || !user?.token) return false;
      const expected = expectedStatus.trim().toLowerCase();
      const startedAt = Date.now();
      while (Date.now() - startedAt < timeoutMs) {
        try {
          const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });
          if (response.ok) {
            const data = (await response.json()) as RoundResponse;
            const statusRaw = data.status ?? data.Status ?? '';
            if (statusRaw.trim().toLowerCase() === expected) {
              return true;
            }
          }
        } catch {
          // Retry until timeout.
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
      return false;
    },
    [apiBaseUrl, roundId, user?.token]
  );

  const handleOpenRound = useCallback(async () => {
    if (!roundId || !user?.token || openingRound || groupSavePending || !!groupSaveError) return;
    if (unevenGroupNumbers.length > 0) {
      setStatusNote(t('roundStartSetup.groupsUneven'));
      return;
    }
    setOpeningRound(true);
    setError(null);
    setStatusNote(t('roundStartSetup.openRoundSending'));
    const normalizedCurrent: Record<string, string> = {};
    players.forEach((player) => {
      const raw = (playingHandicapByPlayerId[player.id] ?? '').trim();
      normalizedCurrent[player.id] = raw;
    });
    const hasShcpChanges = Object.keys(normalizedCurrent).some((playerId) => (savedPlayingHandicapByPlayerId[playerId] ?? '') !== normalizedCurrent[playerId]);
    if (hasShcpChanges) {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players/shcp`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            players: Object.entries(normalizedCurrent).map(([id, scp]) => ({ id, scp })),
          }),
        });
        if (!response.ok) {
          throw new Error(t('roundStartSetup.loadError'));
        }
        setSavedPlayingHandicapByPlayerId((prev) => ({ ...prev, ...normalizedCurrent }));
        setStatusNote(t('roundStartSetup.gamesUpdateSent'));
      } catch (saveError) {
        setOpeningRound(false);
        setError(saveError instanceof Error ? saveError.message : t('roundStartSetup.loadError'));
        return;
      }
    }
    setCurrentRoundId(roundId);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    // Even when the frame is only queued (socket reconnecting) the open is still in flight;
    // waitForRoundStatus below is the bounded wait that decides success or failure.
    socket.send({ type: 'round.status.update', roundId, status: 'active' });
    const confirmed = await waitForRoundStatus('active');
    if (!confirmed) {
      setOpeningRound(false);
      setStatusNote(t('roundStartSetup.openRoundQueued'));
      setError(t('roundStartSetup.openRoundTimeout'));
      return;
    }
    setStatusNote(t('roundStartSetup.openRoundConfirmed'));
    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${encodeURIComponent(tourId)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    markRoundEntry(roundId);
    // Replace, not push: the setup is done, and a pushed hole screen would stack on top of any
    // hole screen this round already has in the navigation history.
    router.replace(`/round/${roundId}/hole/${resolveEntryHole(startHole)}/compact${query}`);
  }, [
    apiBaseUrl,
    debug,
    groupSaveError,
    groupSavePending,
    openingRound,
    players,
    playingHandicapByPlayerId,
    roundId,
    router,
    savedPlayingHandicapByPlayerId,
    startHole,
    t,
    tourId,
    unevenGroupNumbers,
    user?.token,
    waitForRoundStatus,
  ]);

  const handleSelectGames = useCallback(() => {
    if (!roundId) return;
    const params: string[] = ['source=roundstart'];
    // Round settings opened from inside a running round (the bottom-menu entry adds mode=update).
    // Carried through so game selection hands the player back to the hole they were scoring
    // instead of to this setup screen.
    if (updateMode) params.push('mode=update');
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${encodeURIComponent(tourId)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/select-games${query}`);
  }, [debug, roundId, router, tourId, updateMode]);

  const handleOpenShareModal = useCallback(async () => {
    if (!tourId || !user?.token) return;
    setShareModalLoading(true);
    setShareModalError(null);
    setShareModalLink(null);
    setShareModalOpen(true);
    try {
      const inviteLink = await resolveTourInviteLink({
        apiBaseUrl,
        tourId,
        authToken: user.token,
      });
      setShareModalLink(inviteLink);
    } catch (shareError) {
      setShareModalError(t('tournaments.shareLoadError'));
      setStatusNote(t('tournaments.shareLoadError'));
    } finally {
      setShareModalLoading(false);
    }
  }, [apiBaseUrl, t, tourId, user?.token]);

  const shareJoinCode = useMemo(() => {
    if (!shareModalLink) {
      return '';
    }
    try {
      const token = decodeURIComponent(shareModalLink.split('/').pop() ?? '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();
      if (!token) {
        return '';
      }
      const compact = token.slice(0, 6);
      if (compact.length <= 3) {
        return compact;
      }
      return compact;
    } catch {
      return '';
    }
  }, [shareModalLink]);

  const handleShareInviteLink = useCallback(async () => {
    if (!shareModalLink) {
      return;
    }

    const title = t('tournaments.shareTitle', { name: roundName ?? t('tournaments.defaultName') });
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        const navWithShare = navigator as {
          share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
          clipboard?: { writeText?: (value: string) => Promise<void> };
        };

        if (typeof navWithShare.share === 'function') {
          await navWithShare.share({
            title,
            url: shareModalLink,
          });
          return;
        }

        if (navWithShare.clipboard?.writeText) {
          await navWithShare.clipboard.writeText(shareModalLink);
          setStatusNote(t('tournaments.shareCopied'));
          return;
        }

        throw new Error('share unsupported');
      }

      await Share.share({
        title,
        message: shareModalLink,
      });
    } catch (shareError) {
      const message = shareError instanceof Error ? shareError.message : String(shareError);
      const canceled = message.toLowerCase().includes('cancel');
      if (!canceled) {
        setShareModalError(t('tournaments.shareSendError'));
      }
    }
  }, [roundName, shareModalLink, t]);

  useEffect(() => {
    if (!roundId) return;
    setCurrentRoundId(roundId);
  }, [roundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused || !roundId) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    if (!isNavigationReady || !user?.token || !roundId) {
      setLoading(false);
      return;
    }
    void loadSnapshot();
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    const unsubscribeMessages = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'round.updated') {
        const payload = message as RoundUpdatedEvent;
        if (payload.roundId !== roundId) return;
        if (typeof payload.holesCount === 'number') {
          setHolesCount(normalizeHolesCount(payload.holesCount));
        }
        if (payload.gameDefinitionIds) {
          setSelectedGameIds(new Set(payload.gameDefinitionIds));
        }
        if (payload.gameWeights) {
          setWeightsByGameId((prev) => {
            const next = { ...prev };
            payload.gameWeights?.forEach((entry) => {
              if (entry.gameDefinitionId && typeof entry.weightPercent === 'number' && !Number.isNaN(entry.weightPercent)) {
                next[entry.gameDefinitionId] = entry.weightPercent;
              }
            });
            return next;
          });
        }
      } else if (type === 'round.groups.updated') {
        const payload = message as RoundGroupsUpdatedEvent;
        if (payload.roundId !== roundId) return;
        const incomingOrder = (payload.playerOrder ?? []).filter((playerId): playerId is string => typeof playerId === 'string');
        const incomingTeamIndexById = payload.groups
          ? deriveTeamIndexByPlayerId(normalizeGroupsPayload(payload.groups))
          : undefined;
        const incomingRoundTeamIndexById = payload.roundTeams
          ? deriveRoundTeamIndexByPlayerId(normalizeRoundTeamsPayload(payload.roundTeams))
          : undefined;
        if (incomingOrder.length > 0) {
          applyGroupOrder(incomingOrder, incomingTeamIndexById, incomingRoundTeamIndexById);
          lastSavedGroupsRef.current = {
            order: incomingOrder,
            teamIndexById: incomingTeamIndexById ?? teamIndexByIdRef.current,
            roundTeamIndexById: incomingRoundTeamIndexById ?? roundTeamIndexByIdRef.current,
          };
        }
        const pending = pendingGroupUpdateRef.current;
        if (pending && payload.requestId && pending.requestId === payload.requestId) {
          pendingGroupUpdateRef.current = null;
          clearGroupUpdateTimeout();
          setGroupSavePending(false);
          setGroupSaveError(null);
          if (pending.kind === 'mix') {
            setGroupMixStatus('mixed');
          } else {
            setStatusNote(t('roundStartSetup.groupsUpdateSaved'));
          }
        }
      } else if (type === 'round.groups.update.rejected') {
        const payload = message as RoundGroupsUpdateRejectedEvent;
        if (payload.roundId !== roundId) return;
        const pending = pendingGroupUpdateRef.current;
        if (pending && payload.requestId && pending.requestId !== payload.requestId) {
          return;
        }
        // Nothing to mix against yet (the tour's first round): the balls stand as they are.
        if (payload.code === 'round.groups.mix.noHistory') {
          if (pending) {
            pendingGroupUpdateRef.current = null;
            clearGroupUpdateTimeout();
            setGroupSavePending(false);
            setGroupMixStatus('noHistory');
          }
          return;
        }
        if (pending) {
          applyGroupOrder(pending.previousOrder, pending.previousTeamIndexById, pending.previousRoundTeamIndexById);
        }
        pendingGroupUpdateRef.current = null;
        clearGroupUpdateTimeout();
        setGroupSavePending(false);
        setGroupMixStatus(null);
        setSelectedSwapPlayerId(null);
        const messageText = payload.message?.trim() || t('roundStartSetup.groupsUpdateRejected');
        setGroupSaveError(messageText);
        setStatusNote(messageText);
      }
    });
    const unsubscribeErrors = socket.onError((message) => setError(message));
    return () => {
      unsubscribeMessages();
      unsubscribeErrors();
      clearGroupUpdateTimeout();
      pendingGroupUpdateRef.current = null;
    };
  }, [apiBaseUrl, applyGroupOrder, clearGroupUpdateTimeout, isNavigationReady, loadSnapshot, roundId, t, user?.token]);

  useEffect(() => {
    if (!groupLayout) return;
    const timer = setTimeout(() => refreshPlayerCardBounds(), 0);
    return () => clearTimeout(timer);
  }, [groupLayout, refreshPlayerCardBounds]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/" />;
  if (!roundId) return <Redirect href="/my-tours" />;

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={[styles.container, { paddingBottom: 20 + insets.bottom }]}>
      <Text style={styles.subtitle}>
        {roundName ? t('roundStartSetup.subtitleNamed', { name: roundName }) : t('roundStartSetup.subtitleFallback')}
      </Text>
      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={appColors.accent.primary} />
          <Text style={styles.meta}>{t('roundStartSetup.loading')}</Text>
        </View>
      ) : null}
      {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.section}>
        <View style={styles.playersHeaderRow}>
          <Text style={styles.sectionTitle}>{t('roundStartSetup.playersTitle')}</Text>
          <Text style={styles.playersHandicapHeader}>{t('roundStartSetup.playingHandicapLabel')}</Text>
        </View>
        {players.length === 0 ? <Text style={styles.meta}>{t('roundStartSetup.playersEmpty')}</Text> : null}
        {players.map((player) => {
          const enabled = selectedPlayersById[player.id] ?? true;
          return (
            <View key={player.id} style={[styles.row, !enabled && styles.playerRowDisabled]}>
              <Pressable
                style={styles.checkboxWrap}
                onPress={() => handleTogglePlayerSelection(player.id)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: enabled }}
              >
                <View style={[styles.checkbox, enabled && styles.checkboxChecked]}>
                  {enabled ? <Text style={styles.checkboxTick}>✓</Text> : null}
                </View>
              </Pressable>
              <View style={styles.playerInfoBlock}>
                <Text style={[styles.rowTitle, !enabled && styles.rowTitleDisabled]}>{player.name || player.id}</Text>
              </View>
              <View style={styles.handicapWrap}>
                <GoffyInput
                  value={playingHandicapByPlayerId[player.id] ?? ''}
                  onChangeText={(value) => handlePlayingHandicapChange(player.id, value)}
                  placeholder={t('roundStartSetup.playingHandicapPlaceholder')}
                  // The placeholder carries the suggested handicap, so it stays accent-bright
                  // rather than taking the muted default.
                  placeholderTextColor={appColors.accent.primary}
                  keyboardType="numbers-and-punctuation"
                  editable={enabled}
                  size="compact"
                  style={[styles.handicapInput, !enabled && styles.handicapInputDisabled]}
                />
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('players.holesCountLabel')}</Text>
        <View style={styles.holesCountOptions}>
          {holesCountOptions(isDeveloper).map((count) => (
            <Pressable
              key={count}
              style={[styles.holesCountOption, holesCount === count && styles.holesCountOptionActive]}
              onPress={() => handleSelectHolesCount(count)}
              accessibilityLabel={t('players.holesCountOptionLabel', { count })}
            >
              <Text style={[styles.holesCountText, holesCount === count && styles.holesCountTextActive]}>
                {t('players.holesCountOptionValue', { count })}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      {aiEnabled ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('ai.revealMode')}</Text>
          <View style={styles.holesCountOptions}>
            {(['in_advance', 'on_score'] as const).map((mode) => (
              <Pressable
                key={mode}
                style={[styles.holesCountOption, aiRevealMode === mode && styles.holesCountOptionActive]}
                onPress={() => handleSelectAiRevealMode(mode)}
                accessibilityLabel={mode === 'in_advance' ? t('ai.revealInAdvance') : t('ai.revealAsIScore')}
              >
                <Text style={[styles.holesCountText, aiRevealMode === mode && styles.holesCountTextActive]}>
                  {mode === 'in_advance' ? t('ai.revealInAdvance') : t('ai.revealAsIScore')}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      ) : null}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('roundStartSetup.groupsTitle')}</Text>
        <Text style={styles.meta}>
          {groupCanEdit ? t('roundStartSetup.groupsHint') : t('roundStartSetup.groupsLocked')}
        </Text>
        {groupCanEdit && groupLayout ? (
          <GoffyButton
            label={t('roundStartSetup.groupsMix')}
            onPress={handleMixGroups}
            tone="dark"
            size="compact"
            disabled={groupSavePending}
            style={styles.mixGroupsButton}
          />
        ) : null}
        {groupMixStatus === 'mixing' ? <Text style={styles.meta}>{t('roundStartSetup.groupsMixSending')}</Text> : null}
        {groupMixStatus === 'mixed' ? <Text style={styles.meta}>{t('roundStartSetup.groupsMixed')}</Text> : null}
        {groupMixStatus === 'noHistory' ? (
          <Text style={styles.meta}>{t('roundStartSetup.groupsMixNoHistory')}</Text>
        ) : null}
        {groupSavePending && groupMixStatus !== 'mixing' ? (
          <Text style={styles.meta}>{t('roundStartSetup.groupsUpdateSending')}</Text>
        ) : null}
        {groupSaveError ? <Text style={styles.error}>{groupSaveError}</Text> : null}
        {unevenGroupNumbers.length > 0 ? (
          <Text style={styles.error}>{t('roundStartSetup.groupsUneven')}</Text>
        ) : null}
        {!groupLayout ? <Text style={styles.meta}>{t('roundStartSetup.groupsEmpty')}</Text> : null}
        {groupLayout?.groups.map((group, groupIndex) => (
          <View key={group.groupId} style={styles.groupBoard}>
            <Text style={styles.groupTitle}>{t('roundStartSetup.groupLabel', { index: groupIndex + 1 })}</Text>
            {group.teams.map((team, teamIndex) => (
              <View key={team.teamId} style={styles.teamBoard}>
                <Text style={styles.teamTitle}>{t('roundStartSetup.teamLabel', { index: teamIndex + 1 })}</Text>
                <View style={styles.teamPlayers}>
                  {team.playerIds.map((playerId) => (
                    // The drag gesture lives on this wrapper, not on the card itself: Pressable
                    // spreads its own press handlers over any responder props passed to it, so
                    // onResponderGrant/Move/Release on the card were never called. Claiming the
                    // gesture in the *capture* phase, and only on move, leaves a plain tap to the
                    // card's onPress and takes over as soon as the pointer actually travels —
                    // past the tap slop, because an Android tap always moves a pixel or two and
                    // claiming on the first move turned every tap into a drag that went nowhere.
                    <View
                      key={playerId}
                      ref={(node) => {
                        playerCardRefs.current[playerId] = node as MeasurableCard | null;
                      }}
                      style={styles.groupPlayerCardDragWrapper}
                      onLayout={() => refreshPlayerCardBounds()}
                      onTouchStart={(event) => {
                        dragOriginRef.current = { pageX: event.nativeEvent.pageX, pageY: event.nativeEvent.pageY };
                      }}
                      onMoveShouldSetResponderCapture={(event) =>
                        groupCanEdit && !groupSavePending && movedPastTapSlop(dragOriginRef.current, event.nativeEvent)
                      }
                      // Dragging the pointer across the card text selects it, and the responder
                      // system drops the gesture on "selectionchange" unless the responder
                      // refuses. Without this the drag dies mid-move and the drop never lands.
                      onResponderTerminationRequest={() => false}
                      onResponderGrant={() => handleDragStart(playerId)}
                      onResponderMove={(event) =>
                        handleDragMove(playerId, event.nativeEvent.pageX, event.nativeEvent.pageY)
                      }
                      onResponderRelease={(event) =>
                        handleDragRelease(playerId, event.nativeEvent.pageX, event.nativeEvent.pageY)
                      }
                      onResponderTerminate={handleDragCancel}
                    >
                      <Pressable
                        onPress={() => handleGroupPlayerPress(playerId)}
                        disabled={!groupCanEdit || groupSavePending}
                        style={[
                          styles.groupPlayerCard,
                          selectedSwapPlayerId === playerId && styles.groupPlayerCardSelected,
                          draggingPlayerId === playerId && styles.groupPlayerCardDragging,
                          dragHoverPlayerId === playerId && styles.groupPlayerCardDropTarget,
                          (!groupCanEdit || groupSavePending) && styles.groupPlayerCardDisabled,
                        ]}
                      >
                        <Text style={styles.groupPlayerName}>{playerNameById[playerId] ?? playerId}</Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              </View>
            ))}
            {draggingPlayerId !== null &&
            group.teams.length === 1 &&
            group.playerIds.length >= 2 &&
            group.playerIds.includes(draggingPlayerId) ? (
              <View
                ref={(node) => {
                  teamZoneRefs.current[groupIndex] = node as unknown as MeasurableCard | null;
                }}
                onLayout={() => refreshPlayerCardBounds()}
                style={[
                  styles.teamBoard,
                  styles.emptyTeamZone,
                  dragHoverTeamZoneGroupIndex === groupIndex && styles.emptyTeamZoneActive,
                ]}
              >
                <Text style={styles.teamTitle}>{t('roundStartSetup.teamLabel', { index: 2 })}</Text>
                <Text style={styles.meta}>{t('roundStartSetup.groupsNewTeamHint')}</Text>
              </View>
            ) : null}
          </View>
        ))}
      </View>

      {hasRoundTeamGame && groupLayout ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('roundStartSetup.roundTeamsTitle')}</Text>
          <Text style={styles.meta}>
            {groupCanEdit ? t('roundStartSetup.roundTeamsHint') : t('roundStartSetup.groupsLocked')}
          </Text>
          <View style={styles.teamBoard}>
            {groupLayout.effectivePlayerOrder.map((playerId) => {
              const roundTeamIndex = roundTeamIndexById[playerId];
              return (
                <Pressable
                  key={playerId}
                  onPress={() => handleRoundTeamPress(playerId)}
                  disabled={!groupCanEdit || groupSavePending}
                  accessibilityRole="button"
                  style={[
                    styles.groupPlayerCard,
                    styles.roundTeamRow,
                    (!groupCanEdit || groupSavePending) && styles.groupPlayerCardDisabled,
                  ]}
                >
                  <Text style={styles.groupPlayerName}>{playerNameById[playerId] ?? playerId}</Text>
                  <Text style={roundTeamIndex === undefined ? styles.roundTeamChipNone : styles.roundTeamChip}>
                    {roundTeamIndex === undefined
                      ? t('roundStartSetup.roundTeamNone')
                      : t('roundStartSetup.roundTeamLabel', { index: roundTeamIndex + 1 })}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('roundStartSetup.gamesTitle')}</Text>
        {roundGames.length === 0 ? <Text style={styles.meta}>{t('roundStartSetup.gamesEmpty')}</Text> : null}
        {skippedGames.map((game) => (
          <Text key={`skipped-${game.id}`} style={styles.meta} testID={`roundstart-skipped-game-${game.id}`}>
            {t('roundStartSetup.fourballSkipped', { name: game.name || game.id, count: players.length })}
          </Text>
        ))}
        {roundGames.map((game) => {
          const enabled = selectedGameIds.has(game.id);
          const gameImageSource = resolveGameImageSource(game);
          return (
            <View key={game.id} style={styles.row}>
              <Pressable style={styles.checkboxWrap} onPress={() => handleToggleGame(game.id)} accessibilityRole="checkbox" accessibilityState={{ checked: enabled }}>
                <View style={[styles.checkbox, enabled && styles.checkboxChecked]}>
                  {enabled ? <Text style={styles.checkboxTick}>✓</Text> : null}
                </View>
              </Pressable>
              <Pressable style={styles.gameIconWrap} onPress={() => openInfo(game)} accessibilityLabel={t('selectGames.gameInfo')}>
                <Image source={gameImageSource} style={styles.gameIcon} resizeMode="cover" />
              </Pressable>
              <View style={styles.gameInfoBlock}>
                <Text style={styles.rowTitle}>{game.name || game.id}</Text>
                {game.info ? <Text style={styles.gameInfoText} numberOfLines={2}>{game.info}</Text> : null}
              </View>
              <View style={styles.gameActions}>
                <Pressable style={styles.gameInfoButton} onPress={() => openInfo(game)} accessibilityLabel={t('selectGames.gameInfo')}>
                  <Text style={styles.gameInfoButtonText}>{t('selectGames.infoIcon')}</Text>
                </Pressable>
              </View>
            </View>
          );
        })}
      <GoffyButton
        label={t('roundStartSetup.addMoreGamesCta')}
        onPress={handleSelectGames}
          tone="dark"
          oldButtonStyle={styles.secondaryButton}
          oldTextStyle={styles.secondaryButtonText}
          oldDisabledStyle={styles.disabledButton}
        style={styles.primaryButtonWide}
      />
      <GoffyButton
        label={t('tournaments.shareAction')}
        onPress={() => void handleOpenShareModal()}
        tone="dark"
        disabled={!tourId || shareModalLoading}
        size="compact"
        style={styles.shareRoundButton}
      />
      </View>

      <GoffyButton
        label={
          openingRound
            ? t('roundStartSetup.openRoundSending')
            : updateMode
              ? t('roundStartSetup.updateCta')
              : t('roundStartSetup.openRoundCta')
        }
        onPress={handleOpenRound}
        tone="accent"
        disabled={openingRound || groupSavePending || !!groupSaveError || unevenGroupNumbers.length > 0}
        oldButtonStyle={styles.primaryButton}
        oldTextStyle={styles.primaryButtonText}
        oldDisabledStyle={styles.disabledButton}
        style={styles.primaryButtonWide}
      />

      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={closeInfo}
        selectedRuleSetId={
          infoGame
            ? resolveSelectedRuleSetId({ ruleSets: infoGame.rules, roundRuleSetId: ruleSetByGameId[infoGame.id] })
            : null
        }
        onSelectRuleSet={infoGame ? (ruleSetId) => selectGameRuleSet(infoGame.id, ruleSetId) : undefined}
        ruleScope="round"
      />
      <Modal
        transparent
        visible={shareModalOpen}
        animationType="fade"
        onRequestClose={() => setShareModalOpen(false)}
      >
        <Pressable style={styles.shareOverlay} onPress={() => setShareModalOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>{t('players.inviteOthers')}</Text>
            <View style={styles.shareCodeCard}>
              <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
              <Text style={styles.shareCodeValue}>{shareJoinCode || '--- ---'}</Text>
              {shareModalLoading ? (
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              ) : null}
              {shareModalLink ? (
                <View style={styles.shareQrWrapper}>
                  <QRCode value={shareModalLink} size={220} color={appColors.neutral.black} backgroundColor={appColors.neutral.white} />
                </View>
              ) : null}
            </View>
            {shareModalError ? <Text style={styles.error}>{shareModalError}</Text> : null}
            <View style={styles.shareActions}>
              <GoffyButton
                label={t('tournaments.shareAction')}
                onPress={() => void handleShareInviteLink()}
                disabled={!shareModalLink || shareModalLoading}
                tone="accent"
                size="compact"
                style={styles.shareActionButton}
              />
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setShareModalOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: appColors.background.app,
  },
  container: {
    padding: 20,
    gap: 12,
  },
  title: {
    color: appColors.text.secondary,
    fontSize: 24,
    fontWeight: '800',
  },
  subtitle: {
    color: appColors.accent.primary,
    fontSize: 14,
  },
  meta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  error: {
    color: appColors.status.errorTextSoft,
    fontSize: 13,
    fontWeight: '600',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  section: {
    backgroundColor: appColors.background.panel,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  sectionTitle: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  groupBoard: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 10,
    padding: 10,
    gap: 8,
    backgroundColor: appColors.surface.alt,
  },
  groupTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  teamBoard: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 8,
    padding: 8,
    gap: 6,
    backgroundColor: appColors.text.secondary,
  },
  teamTitle: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  teamPlayers: {
    gap: 6,
  },
  groupPlayerCardDragWrapper: {
    // Keep the drag from painting a text selection across the cards it passes over.
    userSelect: 'none',
  },
  groupPlayerCard: {
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: appColors.border.strong,
  },
  groupPlayerCardSelected: {
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.interactive,
  },
  groupPlayerCardDragging: {
    opacity: 0.8,
    borderColor: appColors.status.warningBorder,
  },
  groupPlayerCardDropTarget: {
    borderColor: appColors.border.accent,
    backgroundColor: appColors.text.secondary,
  },
  groupPlayerCardDisabled: {
    opacity: 0.65,
  },
  emptyTeamZone: {
    borderStyle: 'dashed',
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  emptyTeamZoneActive: {
    borderColor: appColors.status.warningBorder,
    backgroundColor: appColors.text.secondary,
  },
  groupPlayerName: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '600',
  },
  roundTeamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  roundTeamChip: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  roundTeamChipNone: {
    color: appColors.text.muted,
    fontSize: 12,
    fontWeight: '600',
  },
  playersHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  playersHandicapHeader: {
    color: appColors.accent.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    backgroundColor: appColors.background.panel,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  rowTitle: {
    color: appColors.text.secondary,
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  rowTitleDisabled: {
    color: appColors.text.muted,
  },
  playerRowDisabled: {
    backgroundColor: appColors.surface.alt,
    borderColor: appColors.border.strong,
    opacity: 0.75,
  },
  playerInfoBlock: {
    flex: 1,
  },
  checkboxWrap: {
    alignSelf: 'center',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.accent,
  },
  checkboxTick: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 16,
  },
  holesCountOptions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  holesCountOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.card,
  },
  holesCountOptionActive: {
    borderColor: appColors.accent.secondary,
    backgroundColor: appColors.accent.secondary,
  },
  holesCountText: {
    color: appColors.text.subtle,
    fontWeight: '600',
  },
  holesCountTextActive: {
    color: appColors.background.panel,
  },
  gameIconWrap: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: appColors.border.strong,
  },
  gameIcon: {
    width: 42,
    height: 42,
  },
  gameInfoBlock: {
    flex: 1,
    gap: 2,
  },
  gameInfoText: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  gameActions: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 6,
  },
  gameInfoButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.surface.light,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameInfoButtonText: {
    color: appColors.surface.tintDeep,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 14,
  },
  handicapWrap: {
    width: 92,
    gap: 0,
  },
  // Frame and fill come from GoffyInput; this is the numeric emphasis only.
  handicapInput: {
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
  handicapInputDisabled: {
    borderColor: appColors.border.strong,
    color: appColors.text.muted,
    opacity: 0.75,
  },
  primaryButton: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.glow, 0.4),
    backgroundColor: alpha(appColors.status.successBg, 0.18),
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  primaryButtonText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  secondaryButton: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.text.secondary,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
  },
  disabledButton: {
    opacity: 0.6,
  },
  primaryButtonWide: {
    width: '100%',
    marginTop: 4,
  },
  shareRoundButton: {
    marginTop: 4,
    alignSelf: 'center',
  },
  mixGroupsButton: {
    marginVertical: 4,
    alignSelf: 'flex-start',
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.22),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.deep,
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: appColors.text.muted,
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: appColors.accent.light,
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: appColors.surface.light,
    padding: 10,
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
});
